// Imports ------------------------------------------------------------------->

import { inject } from '../../services/di'
import { Component } from '../Component'
import { element } from '../CustomElement'

import { Page } from '../flipp-page/Page'
import { LoadingAnimation } from '../flipp-loading/LoadingAnimation'

import {
    ShoppingList,
    ShoppingListService,
} from '../../services/ShoppingListService'
import { AuthenticationService } from '../../services/AuthenticationService'
import { AuthStatus } from '../../services/definitions/IAuthenticationService'
import { ClippingService } from '../../services/ClippingService'
import { LoyaltyProgramService } from '../../services/LoyaltyProgramService'
import { CouponService } from '../../services/CouponService'
import { AnalyticsService } from '../../services/AnalyticsService'
import {
    LoyaltyCard,
    Clipping,
    ClippingType,
} from '../../definitions/CommonDefinitions'
import {
    ClippedCoupon,
    CouponRedemptionStatus,
} from '../../definitions/CouponDefinitions'
import * as array from '../../utils/array'

// Private Definitions ------------------------------------------------------->

interface ShoppingListChunk {
    ShoppingListPage: Component<any, any>
}

// Public Definitions -------------------------------------------------------->

@element('flipp-shopping-list-entry')
export class ShoppingListEntry extends Component<any, ShoppingListChunk> {
    private static chunkLoaded = false

    @inject private shoppingList: ShoppingListService
    @inject private auth: AuthenticationService
    @inject private couponsService: CouponService
    @inject private clippingService: ClippingService
    @inject private cardsService: LoyaltyProgramService
    @inject private analytics: AnalyticsService

    prepare() {
        return new Promise<ShoppingListChunk>((resolve) => {
            require.ensure(['./ShoppingListPage'], () => {
                /* eslint-disable @typescript-eslint/no-var-requires */
                const chunk = require('./ShoppingListPage') as ShoppingListChunk
                ShoppingListEntry.chunkLoaded = true
                resolve(chunk)
                /* eslint-enable @typescript-eslint/no-var-requires */
            })
        })
    }

    renderLoading() {
        if (ShoppingListEntry.chunkLoaded) {
            return null
        }

        return (
            <Page show="flyers">
                <div flex-vertical fill-absolute>
                    <section class="main">
                        <LoadingAnimation />
                    </section>
                </div>
            </Page>
        )
    }

    onRender() {
        this._getSLDetails().then(([list, couponCount]) => {
            this.analytics.trackShoppingListView(
                list.items.length,
                list.photos.length,
                list.flyerItemClippings.length,
                list.ecomItems.length,
                couponCount
            )
        })
    }

    render(model: ShoppingListChunk) {
        const ShoppingListPage = model.ShoppingListPage as any
        return (
            <Page show="flyers" context="shoppingList">
                <ShoppingListPage />
            </Page>
        )
    }

    private _getSLDetails = () => {
        let slJoinFn
        if (
            this.auth.status === AuthStatus.LOGGED_IN &&
            !this.shoppingList.localList.isDelegate
        ) {
            slJoinFn = this.shoppingList.joinLocalList(this.auth.user.token)
        } else {
            slJoinFn = Promise.resolve(this.shoppingList.localList)
        }

        let getLoyaltyCardsFn = Promise.resolve<LoyaltyCard[]>([])
        let getClippedCouponsFn = Promise.resolve<ClippedCoupon[]>([])
        if (this.auth.status === AuthStatus.LOGGED_IN) {
            getLoyaltyCardsFn = this.cardsService.getLoyaltyCards(
                this.auth.user
            )
            getClippedCouponsFn = this.couponsService.getClippedCoupons(
                this.auth.user
            )
        } else {
            slJoinFn = Promise.resolve(this.shoppingList.localList)
        }

        return Promise.all([
            this.clippingService.getClippings(),
            this.couponsService.getCoupons(),
            this.cardsService.getLoyaltyPrograms(),
            getLoyaltyCardsFn,
            getClippedCouponsFn,
            slJoinFn,
        ]).then<[ShoppingList, number]>(
            ([
                clippings,
                coupons,
                programs,
                loyaltyCards,
                clippedCoupons,
                shoppingList,
            ]) => {
                const validCoupons = clippedCoupons
                    .filter(
                        (clippedCoupon) =>
                            clippedCoupon.status ===
                            CouponRedemptionStatus.CLIPPED
                    )
                    .filter((clippedCoupon) => {
                        const card = array.find(
                            loyaltyCards,
                            (card) =>
                                clippedCoupon.program.loyaltyProgramId ===
                                card.loyaltyProgramId
                        )
                        const program = array.find(
                            programs,
                            (program) =>
                                program.id ===
                                clippedCoupon.program.loyaltyProgramId
                        )
                        const coupon = array.find(
                            coupons,
                            (coupon) =>
                                coupon.id === clippedCoupon.program.couponId
                        )
                        return card && program && coupon
                    })

                /* Create a map of coupon ids for easy lookup */
                const couponIds: { [key: number]: boolean } = {}
                coupons.forEach((coupon) => (couponIds[coupon.id] = true))

                const prunedClippings: Clipping[] = []
                clippings.forEach((clipping) => {
                    /* Don't show print coupons that are not available in the current locale */
                    if (
                        clipping.type === ClippingType.PRINT_COUPON &&
                        couponIds[Number(clipping.item.id)]
                    ) {
                        prunedClippings.push(clipping)
                    } else if (clipping.type !== ClippingType.PRINT_COUPON) {
                        prunedClippings.push(clipping)
                    }
                })

                return [
                    shoppingList,
                    validCoupons.length + prunedClippings.length,
                ]
            }
        )
    }
}
